import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActionIcon,
  Button,
  Group,
  Loader,
  NumberInput,
  Paper,
  Select,
  Stack,
  Text,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconChefHat, IconPlus, IconTrash } from '@tabler/icons-react';
import { isAxiosError } from 'axios';
import { useItems } from '../items/itemsApi';
import { useRecipe, useReplaceRecipe } from './recipesApi';
import type { ReplaceRecipeIngredientRequest } from './types';

interface Props {
  /** The composite item being edited. */
  itemId: string;
}

interface Row {
  /** Stable React key; not sent to the server. */
  uid: string;
  ingredientItemId: string | null;
  quantity: number;
}

// Inline editor mounted under the Item drawer for Composite items only.
// Owns its own save button — the parent drawer's Save only persists the
// item fields, not the recipe (separation keeps each network call's
// success/failure independent so the operator can see exactly which
// step failed).
export function RecipePanel({ itemId }: Props) {
  const { t, i18n } = useTranslation();
  const recipeQuery = useRecipe(itemId);
  const itemsQuery = useItems();
  const replace = useReplaceRecipe();

  const [rows, setRows] = useState<Row[]>([]);

  // Hydrate the local list from server data each time the recipe loads.
  useEffect(() => {
    if (!recipeQuery.data) return;
    setRows(
      recipeQuery.data.ingredients.map((i) => ({
        uid: i.recipeIngredientId,
        ingredientItemId: i.ingredientItemId,
        quantity: i.quantity,
      })),
    );
  }, [recipeQuery.data]);

  // Ingredient picker — exclude self (an item can't be in its own
  // recipe) and items already chosen in this recipe so the operator
  // can't duplicate-pick (server rejects duplicates anyway, but the
  // UI shouldn't offer them).
  const ingredientOptions = useMemo(() => {
    const all = itemsQuery.data ?? [];
    const taken = new Set(
      rows
        .map((r) => r.ingredientItemId)
        .filter((id): id is string => id !== null),
    );
    return all
      .filter((i) => i.itemId !== itemId)
      .map((i) => {
        const label = `${i.sku} — ${i.nameAr}`;
        return {
          value: i.itemId,
          label: i18n.language === 'ar' ? label : (i.nameEn ?? i.nameAr),
          disabled: taken.has(i.itemId),
        };
      });
  }, [itemsQuery.data, rows, itemId, i18n.language]);

  const addRow = () =>
    setRows([
      ...rows,
      { uid: crypto.randomUUID(), ingredientItemId: null, quantity: 1 },
    ]);

  const removeRow = (uid: string) => setRows(rows.filter((r) => r.uid !== uid));

  const updateRow = (uid: string, patch: Partial<Row>) =>
    setRows(rows.map((r) => (r.uid === uid ? { ...r, ...patch } : r)));

  const save = async () => {
    const valid = rows.filter(
      (r): r is Row & { ingredientItemId: string } =>
        r.ingredientItemId !== null && r.quantity > 0,
    );

    const payload: ReplaceRecipeIngredientRequest[] = valid.map((r, i) => ({
      ingredientItemId: r.ingredientItemId,
      quantity: r.quantity,
      sortOrder: i,
    }));

    try {
      await replace.mutateAsync({ itemId, request: { ingredients: payload } });
      notifications.show({
        color: 'green',
        title: t('common.toasts.updated'),
        message: t('recipes.panel.savedMessage'),
      });
    } catch (err) {
      notifications.show({
        color: 'red',
        title: t('common.toasts.errorTitle'),
        message: extractApiMessage(err) ?? t('common.toasts.errorBody'),
      });
    }
  };

  if (recipeQuery.isLoading || itemsQuery.isLoading) {
    return (
      <Paper p="md" radius="sm">
        <Group justify="center" py="md">
          <Loader size="sm" />
        </Group>
      </Paper>
    );
  }

  return (
    <Paper p="md" radius="sm">
      <Group justify="space-between" mb="sm">
        <div>
          <Text fw={500} c="var(--wheels-ink-strong)">
            {t('recipes.panel.title')}
          </Text>
          <Text fz="xs" c="var(--wheels-ink-muted)">
            {t('recipes.panel.help')}
          </Text>
        </div>
        <Button
          variant="subtle"
          size="xs"
          leftSection={<IconPlus size={14} stroke={1.8} />}
          onClick={addRow}
        >
          {t('recipes.panel.add')}
        </Button>
      </Group>

      {rows.length === 0 ? (
        <Group gap="xs" c="var(--wheels-ink-muted)">
          <IconChefHat size={16} stroke={1.6} />
          <Text fz="sm">{t('recipes.panel.empty')}</Text>
        </Group>
      ) : (
        <Stack gap="xs">
          {rows.map((row) => (
            <Group key={row.uid} gap="xs" wrap="nowrap" align="flex-end">
              <Select
                label={t('recipes.panel.ingredientLabel')}
                placeholder={t('recipes.panel.ingredientPlaceholder')}
                data={ingredientOptions}
                value={row.ingredientItemId}
                onChange={(v) => updateRow(row.uid, { ingredientItemId: v })}
                searchable
                style={{ flex: 2 }}
              />
              <NumberInput
                label={t('recipes.panel.quantityLabel')}
                value={row.quantity}
                onChange={(v) =>
                  updateRow(row.uid, { quantity: typeof v === 'number' ? v : 0 })
                }
                decimalScale={4}
                min={0}
                step={0.1}
                style={{ flex: 1 }}
              />
              <ActionIcon
                variant="subtle"
                color="red"
                onClick={() => removeRow(row.uid)}
                aria-label={t('common.delete')}
              >
                <IconTrash size={16} stroke={1.6} />
              </ActionIcon>
            </Group>
          ))}
        </Stack>
      )}

      <Group justify="flex-end" mt="md">
        <Button size="xs" loading={replace.isPending} onClick={save}>
          {t('recipes.panel.save')}
        </Button>
      </Group>
    </Paper>
  );
}

function extractApiMessage(err: unknown): string | null {
  if (isAxiosError(err)) {
    const data = err.response?.data as { detail?: string; title?: string } | undefined;
    return data?.detail ?? data?.title ?? err.message;
  }
  return null;
}
